import React from "react";
import { Check, ChevronsUpDown, Plus, Trash2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PROJECT_LABELS, unitPlural } from "@/lib/projectTypes";

/**
 * Come si riassume un progetto in una riga.
 *
 * Un documento che si sta scrivendo si riassume con quanto ne è fatto; uno
 * portato qui per essere riletto è fatto per intero, e l'unica misura che
 * significhi qualcosa è quanto è lungo - le pagine che ha in originale.
 */
function riassunto(thesis) {
  if (!thesis) return "Creane uno per iniziare";
  const parti = `${thesis.chapters?.length || 0} ${unitPlural(thesis.project_type).toLowerCase()}`;
  const pagine = (thesis.chapters || []).reduce(
    (massimo, c) => Math.max(massimo, Number(c.end_page) || 0), 0,
  );
  if (thesis.project_mode === "review") {
    return pagine ? `${parti} · ${pagine} pagine` : parti;
  }
  return `${parti} · ${thesis.completion_percentage || 0}%`;
}

export default function ThesisSelector({ thesis, theses, onSelect, onCreate, onDelete }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="thesis-selector-trigger"
          className="flex w-full items-center gap-2 rounded-md border border-line px-2.5 py-2 text-left transition-colors hover:bg-surface-2"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13.5px] font-medium text-ink">
              {thesis?.title || "Nessun progetto"}
            </span>
            <span className="block truncate text-2xs text-ink-subtle">
              {riassunto(thesis)}
            </span>
          </span>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="bottom" sideOffset={6} className="w-[264px] p-1">
        <p className="numa-eyebrow px-2 py-1.5">Progetti</p>

        {theses.length === 0 && (
          <p className="px-2 py-2 text-[12.5px] text-ink-muted">Nessun progetto salvato.</p>
        )}

        {theses.map((t) => {
          const active = t.thesis_id === thesis?.thesis_id;
          return (
            <DropdownMenuItem
              key={t.thesis_id}
              data-testid={`thesis-option-${t.thesis_id}`}
              onSelect={(e) => { e.preventDefault(); onSelect(t); }}
              className="flex cursor-pointer items-center gap-2 px-2 py-1.5"
            >
              <span className="min-w-0 flex-1">
                <span className={`block truncate text-[13px] ${active ? "font-medium text-brand" : "text-ink"}`}>
                  {t.title}
                </span>
                <span className="block truncate text-2xs text-ink-subtle">
                  {PROJECT_LABELS[t.project_type] || "Documento"} · {t.completion_percentage || 0}%
                </span>
              </span>
              {active && <Check className="h-3.5 w-3.5 shrink-0 text-brand" />}
              {onDelete && (
                <button
                  type="button"
                  data-testid={`thesis-delete-${t.thesis_id}`}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(t); }}
                  title={`Elimina"${t.title}"`}
                  className="rounded p-1 text-ink-subtle transition-colors hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  <span className="sr-only">Elimina {t.title}</span>
                </button>
              )}
            </DropdownMenuItem>
          );
        })}

        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="new-thesis-btn"
          onClick={onCreate}
          className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-[13px] font-medium text-brand"
        >
          <Plus className="h-3.5 w-3.5" /> Nuovo progetto
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
